import { 
  FiX, FiHome, FiFileText, FiPackage, FiUsers, 
  FiShoppingCart, FiCreditCard, FiBarChart, FiBox, 
  FiSettings, FiGrid, FiTruck, FiDollarSign, FiMaximize2
} from 'react-icons/fi'
import PageRefreshButton from './PageRefreshButton'
import { useTabStore } from '../../store/tabStore'
import { useLanguageStore } from '../../store/languageStore'
import { useUiLayoutStore } from '../../store/uiLayoutStore'
import { useTranslation } from '../../hooks/useTranslation'
import { useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import ConfirmationDialog from '../common/ConfirmationDialog'
import { clearPersistentStatesForPath, hasPersistedStatesForPath } from '../../hooks/usePersistentFormState'
import { destroyAllKeepAlive, destroyKeepAliveForTab, destroyKeepAliveForTabs } from './keepAliveBridge'

type ContextMenuState = {
  x: number
  y: number
  tabId: string
  tabTitle: string
} | null

export default function InternalTabs() {
  const {
    tabs,
    activeTabId,
    setActiveTab,
    removeTab,
    closeOtherTabs,
    closeTabsToSide,
    clearTabs,
  } = useTabStore()
  const { language } = useLanguageStore()
  const { translate } = useTranslation()
  const { toggleContentFullscreen } = useUiLayoutStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isRTL = language === 'ar'
  const menuRef = useRef<HTMLDivElement>(null)

  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null)
  const [confirmClose, setConfirmClose] = useState<{ isOpen: boolean; tabId: string; tabTitle: string; hasDraft: boolean }>({
    isOpen: false,
    tabId: '',
    tabTitle: '',
    hasDraft: false,
  })

  const withTitle = (key: string, title: string) =>
    translate(key).replace('{title}', title)

  useEffect(() => {
    if (!contextMenu) return

    const handleClose = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setContextMenu(null)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    const handleScroll = () => setContextMenu(null)

    document.addEventListener('mousedown', handleClose)
    document.addEventListener('keydown', handleEscape)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('mousedown', handleClose)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [contextMenu])

  const getTabIcon = (id: string) => {
    switch (id) {
      case 'dashboard': return <FiHome className="text-lg" />
      case 'items': return <FiPackage className="text-lg" />
      case 'customers': return <FiUsers className="text-lg" />
      case 'invoices': return <FiFileText className="text-lg" />
      case 'pos': return <FiShoppingCart className="text-lg" />
      case 'reports': return <FiBarChart className="text-lg" />
      case 'inventory': return <FiBox className="text-lg" />
      case 'settings': return <FiSettings className="text-lg" />
      case 'purchases': return <FiTruck className="text-lg" />
      case 'purchase-invoices': return <FiTruck className="text-lg" />
      case 'purchaseInvoices': return <FiTruck className="text-lg" />
      case 'accounting': return <FiCreditCard className="text-lg" />
      case 'categories': return <FiGrid className="text-lg" />
      case 'price-list': return <FiDollarSign className="text-lg" />
      default: return <FiFileText className="text-lg" />
    }
  }

  const navigateToActiveTab = () => {
    const newActiveTabId = useTabStore.getState().activeTabId
    const newActiveTab = useTabStore.getState().tabs.find(t => t.id === newActiveTabId)
    if (newActiveTab && location.pathname !== newActiveTab.lastPath) {
      navigate(newActiveTab.lastPath)
    }
  }

  const clearDraftsForTabs = (tabIds: string[]) => {
    tabIds.forEach(id => {
      const tab = tabs.find(t => t.id === id)
      if (tab?.lastPath) clearPersistentStatesForPath(tab.lastPath)
    })
  }

  const handleTabClick = (tabId: string, lastPath: string) => {
    setActiveTab(tabId)
    if (location.pathname !== lastPath) {
      navigate(lastPath)
    }
  }

  const handleCloseTabRequest = (e: React.MouseEvent, tabId: string, tabTitle: string) => {
    e.stopPropagation()
    const closingTab = tabs.find(t => t.id === tabId)
    const hasDraft = closingTab?.lastPath ? hasPersistedStatesForPath(closingTab.lastPath) : false
    setConfirmClose({
      isOpen: true,
      tabId,
      tabTitle,
      hasDraft,
    })
  }

  const handleConfirmClose = () => {
    const { tabId } = confirmClose
    const closingTab = tabs.find(t => t.id === tabId)
    const isActive = activeTabId === tabId
    destroyKeepAliveForTab(tabId)
    removeTab(tabId)

    if (closingTab?.lastPath) {
      clearPersistentStatesForPath(closingTab.lastPath)
    }
    
    if (isActive) {
      navigateToActiveTab()
    }

    setConfirmClose({ isOpen: false, tabId: '', tabTitle: '', hasDraft: false })
  }

  const handleContextMenu = (e: React.MouseEvent, tabId: string, tabTitle: string) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({ x: e.clientX, y: e.clientY, tabId, tabTitle })
  }

  const runBulkClose = (action: 'others' | 'left' | 'right' | 'all') => {
    if (!contextMenu) return
    const { tabId } = contextMenu
    const before = useTabStore.getState().tabs
    const beforeIds = new Set(before.map(t => t.id))

    if (action === 'others') {
      closeOtherTabs(tabId)
      setActiveTab(tabId)
      const keep = before.find(t => t.id === tabId)
      if (keep && location.pathname !== keep.lastPath) {
        navigate(keep.lastPath)
      }
    } else if (action === 'left' || action === 'right') {
      closeTabsToSide(tabId, action, isRTL)
      navigateToActiveTab()
    } else {
      clearTabs()
      navigate('/dashboard')
    }

    const after = useTabStore.getState().tabs
    const afterIds = new Set(after.map(t => t.id))
    const closedIds = [...beforeIds].filter(id => !afterIds.has(id))
    clearDraftsForTabs(closedIds)
    if (action === 'all') {
      destroyAllKeepAlive()
    } else {
      destroyKeepAliveForTabs(closedIds)
    }

    setContextMenu(null)
  }

  const contextTabIndex = contextMenu ? tabs.findIndex(t => t.id === contextMenu.tabId) : -1
  const closableCount = tabs.filter(t => t.closable !== false).length
  const canCloseOthers = contextMenu
    ? tabs.some(t => t.id !== contextMenu.tabId && t.closable !== false)
    : false
  const canCloseLeft = contextTabIndex >= 0 && (
    isRTL
      ? tabs.slice(contextTabIndex + 1).some(t => t.closable !== false)
      : tabs.slice(0, contextTabIndex).some(t => t.closable !== false)
  )
  const canCloseRight = contextTabIndex >= 0 && (
    isRTL
      ? tabs.slice(0, contextTabIndex).some(t => t.closable !== false)
      : tabs.slice(contextTabIndex + 1).some(t => t.closable !== false)
  )
  const canCloseAll = closableCount > 0
  const contextTab = contextMenu ? tabs.find(t => t.id === contextMenu.tabId) : null
  const canCloseThis = contextTab?.closable !== false

  if (tabs.length === 0) return null

  const menuItems = [
    {
      key: 'close',
      label: translate('tabs.close'),
      disabled: !canCloseThis,
      onClick: () => {
        if (!contextMenu || !canCloseThis) return
        const tab = tabs.find(t => t.id === contextMenu.tabId)
        setContextMenu(null)
        if (tab) {
          const hasDraft = tab.lastPath ? hasPersistedStatesForPath(tab.lastPath) : false
          setConfirmClose({
            isOpen: true,
            tabId: tab.id,
            tabTitle: tab.title,
            hasDraft,
          })
        }
      },
    },
    {
      key: 'others',
      label: translate('tabs.closeOthers'),
      disabled: !canCloseOthers,
      onClick: () => runBulkClose('others'),
    },
    {
      key: 'right',
      label: translate('tabs.closeToTheRight'),
      disabled: !canCloseRight,
      onClick: () => runBulkClose('right'),
    },
    {
      key: 'left',
      label: translate('tabs.closeToTheLeft'),
      disabled: !canCloseLeft,
      onClick: () => runBulkClose('left'),
    },
    {
      key: 'all',
      label: translate('tabs.closeAll'),
      disabled: !canCloseAll,
      onClick: () => runBulkClose('all'),
      danger: true,
    },
  ]

  const menuStyle = contextMenu
    ? {
        top: Math.min(contextMenu.y, window.innerHeight - 220),
        left: isRTL
          ? Math.max(8, Math.min(contextMenu.x - 200, window.innerWidth - 220))
          : Math.min(contextMenu.x, window.innerWidth - 220),
      }
    : undefined

  return (
    <>
      <div className="sticky top-0 z-30 bg-gray-100/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 px-4 flex items-end gap-1 overflow-x-auto overflow-y-hidden no-scrollbar pt-2 min-h-[52px] shadow-sm">
        {tabs.map((tab) => {
          const isActive = activeTabId === tab.id
          return (
            <div
              key={tab.id}
              onClick={() => handleTabClick(tab.id, tab.lastPath)}
              onContextMenu={(e) => handleContextMenu(e, tab.id, tab.title)}
              className={`
                group relative flex items-center gap-1.5 px-3 py-2.5 rounded-t-xl cursor-pointer transition-all duration-300 min-w-[120px] max-w-[280px]
                ${isActive 
                  ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 font-bold shadow-[0_-4px_10px_rgba(0,0,0,0.05)] border-t-2 border-blue-500 -mb-[1px] z-10' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-800/50 hover:text-gray-700 dark:hover:text-gray-200'
                }
              `}
            >
              <span className={`${isActive ? 'scale-110' : 'scale-100'} transition-transform duration-300`}>
                {getTabIcon(tab.id)}
              </span>
              
              <span className="truncate text-sm flex-1">
                {tab.id === 'dashboard' ? translate('tabs.home') : tab.title}
              </span>

              {isActive && (
                <>
                  <PageRefreshButton
                    stopPropagation
                    iconSize={14}
                    className="p-1 rounded-full text-emerald-600 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-900/40 transition-all duration-200 disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleContentFullscreen()
                    }}
                    className="p-1 rounded-full text-violet-600 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-all duration-200"
                    title={translate('tabs.fullscreen')}
                  >
                    <FiMaximize2 size={14} strokeWidth={3} />
                  </button>
                </>
              )}
              
              {tab.closable !== false && (
                <button
                  onClick={(e) => handleCloseTabRequest(e, tab.id, tab.title)}
                  className={`
                    p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200
                    ${isActive ? 'text-blue-500 opacity-100' : 'text-gray-400 opacity-0 group-hover:opacity-100'}
                  `}
                >
                  <FiX size={14} strokeWidth={3} />
                </button>
              )}

              {isActive && (
                <div className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800" />
              )}
            </div>
          )
        })}
      </div>

      {contextMenu && (
        <div
          ref={menuRef}
          className="fixed z-[100] min-w-[200px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl py-1.5 overflow-hidden"
          style={menuStyle}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {menuItems.map((item, index) => (
            <div key={item.key}>
              {item.key === 'all' && (
                <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
              )}
              <button
                type="button"
                disabled={item.disabled}
                onClick={item.onClick}
                className={`
                  w-full px-3.5 py-2 text-sm text-start transition-colors
                  ${item.disabled
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                    : item.danger
                      ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                  }
                `}
              >
                {item.label}
              </button>
              {index === 0 && (
                <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmClose.isOpen}
        onClose={() => setConfirmClose({ ...confirmClose, isOpen: false })}
        onConfirm={handleConfirmClose}
        title={translate('tabs.closePage')}
        message={
          confirmClose.hasDraft
            ? withTitle('tabs.closeConfirmUnsaved', confirmClose.tabTitle)
            : withTitle('tabs.closeConfirm', confirmClose.tabTitle)
        }
        confirmText={translate('tabs.confirmClose')}
        cancelText={translate('tabs.cancel')}
        type="warning"
      />
    </>
  )
}
